import { motion } from "framer-motion";
import {
    AlertOctagon,
    ArrowDown,
    ArrowUp,
    ClipboardEdit,
    Copy,
    GripVertical,
    Loader2,
    Pencil,
    Plus,
    RotateCcw,
    Save,
    Sparkles,
    Star,
    Trash2,
    X,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";

import { api } from "../lib/api";

const PHASES = [
    { id: "verification", label: "Vérification éligibilité", color: "bg-blue-50 text-blue-700 border-blue-200" },
    { id: "discovery", label: "Découverte & motivation", color: "bg-shariti-orange-soft text-shariti-orange border-shariti-orange/30" },
    { id: "mental", label: "Test mental & résilience", color: "bg-violet-50 text-violet-700 border-violet-200" },
    { id: "projection", label: "Projection", color: "bg-emerald-50 text-emerald-700 border-emerald-200" },
    { id: "observation", label: "Observation recruteur", color: "bg-slate-100 text-slate-600 border-slate-200" },
];

const PHASE_BY_ID = Object.fromEntries(PHASES.map((p) => [p.id, p]));

const KINDS = [
    { id: "rating", label: "Note 1-5 (score auto)" },
    { id: "rating_gating", label: "Note 1-5 + éliminatoire si ≤ 2" },
    { id: "yesno_gating", label: "Oui / Non éliminatoire" },
    { id: "yesno_text", label: "Oui / Non + précision libre" },
    { id: "text", label: "Texte libre uniquement" },
];

const EMPTY_ITEM = {
    phase: "discovery",
    q: "",
    kind: "rating",
    criterion_ids: [],
    hint: "",
    criterion_hint: "",
    no_speak: false,
    with_comment: false,
};

const CRITERIA_FALLBACK = [
    "energie_vocale", "fluidite_orale", "bagout", "motivation", "disponibilite",
    "reactivite", "resilience", "maturite", "mobilite", "sensibilite_cause",
    "bienveillance", "coachabilite",
];

export default function AdminEntretienScript() {
    const [trames, setTrames] = useState([]);
    const [activeTrameId, setActiveTrameId] = useState(null);
    const [items, setItems] = useState([]);
    const [criteriaIds, setCriteriaIds] = useState(CRITERIA_FALLBACK);
    const [loading, setLoading] = useState(true);
    const [loadingItems, setLoadingItems] = useState(false);
    const [editing, setEditing] = useState(null);
    const [creating, setCreating] = useState(false);
    const [saving, setSaving] = useState(false);
    const [resetting, setResetting] = useState(false);
    const [trameEditor, setTrameEditor] = useState(null); // { mode: 'create'|'rename', name, description, copy_from_id }
    const [draggedId, setDraggedId] = useState(null);
    const [dropTargetId, setDropTargetId] = useState(null);

    const activeTrame = trames.find((t) => t.id === activeTrameId);

    const loadTrames = useCallback(async () => {
        const [{ data: tr }, { data: critsData }] = await Promise.all([
            api.get("/entretien-trames"),
            api.get("/candidates/criteria").catch(() => ({ data: null })),
        ]);
        setTrames(tr);
        if (critsData?.prerec) setCriteriaIds(critsData.prerec.map((c) => c.id));
        if (!activeTrameId && tr.length > 0) {
            const defaultT = tr.find((t) => t.is_default) || tr[0];
            setActiveTrameId(defaultT.id);
        }
    }, [activeTrameId]);

    const loadItems = useCallback(async () => {
        if (!activeTrameId) return;
        setLoadingItems(true);
        try {
            const { data } = await api.get("/entretien-script", { params: { trame_id: activeTrameId } });
            setItems(data);
        } catch {
            toast.error("Impossible de charger les questions");
        } finally {
            setLoadingItems(false);
        }
    }, [activeTrameId]);

    useEffect(() => { loadTrames().finally(() => setLoading(false)); }, [loadTrames]);
    useEffect(() => { loadItems(); }, [loadItems]);

    const grouped = useMemo(() => {
        const map = new Map(PHASES.map((p) => [p.id, []]));
        items.forEach((it) => {
            if (!map.has(it.phase)) map.set(it.phase, []);
            map.get(it.phase).push(it);
        });
        return Array.from(map.entries());
    }, [items]);

    async function move(item, dir) {
        const idx = items.findIndex((i) => i.id === item.id);
        const swapIdx = dir === "up" ? idx - 1 : idx + 1;
        if (swapIdx < 0 || swapIdx >= items.length) return;
        const next = items.slice();
        [next[idx], next[swapIdx]] = [next[swapIdx], next[idx]];
        setItems(next);
        try {
            await api.post("/entretien-script/reorder", { ids: next.map((i) => i.id) });
        } catch {
            toast.error("Réorganisation impossible");
            await loadItems();
        }
    }

    async function persistOrder(orderedIds) {
        try {
            await api.post("/entretien-script/reorder", { ids: orderedIds });
        } catch {
            toast.error("Réorganisation impossible");
            await loadItems();
        }
    }

    function handleDragStart(e, itemId) {
        setDraggedId(itemId);
        e.dataTransfer.effectAllowed = "move";
        // Required for Firefox
        try { e.dataTransfer.setData("text/plain", itemId); } catch { /* noop */ }
    }

    function handleDragOver(e, overId) {
        if (!draggedId || draggedId === overId) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = "move";
        if (dropTargetId !== overId) setDropTargetId(overId);
    }

    function handleDrop(e, overId) {
        e.preventDefault();
        if (!draggedId || draggedId === overId) {
            setDraggedId(null);
            setDropTargetId(null);
            return;
        }
        const fromIdx = items.findIndex((i) => i.id === draggedId);
        const toIdx = items.findIndex((i) => i.id === overId);
        if (fromIdx === -1 || toIdx === -1) return;
        const next = items.slice();
        const [moved] = next.splice(fromIdx, 1);
        next.splice(toIdx, 0, moved);
        setItems(next);
        setDraggedId(null);
        setDropTargetId(null);
        persistOrder(next.map((i) => i.id));
    }

    function handleDragEnd() {
        setDraggedId(null);
        setDropTargetId(null);
    }

    async function remove(item) {
        if (!window.confirm(`Supprimer la question :\n« ${item.q} » ?`)) return;
        try {
            await api.delete(`/entretien-script/${item.id}`);
            setItems((prev) => prev.filter((i) => i.id !== item.id));
            toast.success("Question supprimée");
        } catch {
            toast.error("Impossible de supprimer");
        }
    }

    async function resetDefaults() {
        if (!window.confirm("Remettre cette trame à son état d'origine ? Toutes les modifications de cette trame seront perdues.")) return;
        setResetting(true);
        try {
            await api.post("/entretien-script/reset-defaults", null, { params: { trame_id: activeTrameId } });
            await loadItems();
            await loadTrames();
            toast.success("Trame d'origine restaurée");
        } catch {
            toast.error("Impossible de réinitialiser");
        } finally {
            setResetting(false);
        }
    }

    async function saveEdit(payload) {
        setSaving(true);
        try {
            if (creating) {
                const { data } = await api.post("/entretien-script", payload, {
                    params: { trame_id: activeTrameId },
                });
                setItems((prev) => [...prev, data]);
                toast.success("Question ajoutée");
            } else {
                const { data } = await api.patch(`/entretien-script/${editing.id}`, payload);
                setItems((prev) => prev.map((i) => (i.id === editing.id ? data : i)));
                toast.success("Question mise à jour");
            }
            setEditing(null);
            setCreating(false);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Erreur");
        } finally {
            setSaving(false);
        }
    }

    async function deleteTrame(t) {
        if (t.is_default) {
            toast.error("Impossible de supprimer la trame par défaut");
            return;
        }
        if (!window.confirm(`Supprimer la trame "${t.name}" et toutes ses questions ?`)) return;
        try {
            await api.delete(`/entretien-trames/${t.id}`);
            const remaining = trames.filter((x) => x.id !== t.id);
            setTrames(remaining);
            if (activeTrameId === t.id) {
                setActiveTrameId(remaining.find((x) => x.is_default)?.id || remaining[0]?.id || null);
            }
            toast.success("Trame supprimée");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Erreur");
        }
    }

    async function saveTrame(payload, mode) {
        try {
            if (mode === "create") {
                const { data } = await api.post("/entretien-trames", payload);
                setTrames((prev) => [...prev, data]);
                setActiveTrameId(data.id);
                toast.success(`Trame "${data.name}" créée`);
            } else {
                const { data } = await api.patch(`/entretien-trames/${activeTrameId}`, {
                    name: payload.name,
                    description: payload.description,
                });
                setTrames((prev) => prev.map((t) => (t.id === activeTrameId ? data : t)));
                toast.success("Trame renommée");
            }
            setTrameEditor(null);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Erreur");
        }
    }

    if (loading) {
        return (
            <div className="min-h-[50vh] flex items-center justify-center">
                <Loader2 className="h-6 w-6 animate-spin text-shariti-orange" />
            </div>
        );
    }

    return (
        <div className="space-y-6" data-testid="admin-entretien-script">
            <header className="flex flex-col gap-4">
                <div>
                    <div className="inline-flex items-center gap-2 rounded-full bg-shariti-orange-soft text-shariti-orange px-3 py-1 text-[10px] font-bold uppercase tracking-widest">
                        <ClipboardEdit className="h-3 w-3" /> Back-office
                    </div>
                    <h1 className="mt-3 text-3xl md:text-4xl font-black tracking-tight text-shariti-black">
                        Trames d'entretien
                    </h1>
                    <p className="mt-2 text-sm font-medium text-slate-500 max-w-3xl">
                        Créez plusieurs trames d'entretien (ex : missions fixes vs missions itinérantes) et associez chacune à une offre Layan depuis la page <strong>Offres</strong>. Quand un candidat postule, la trame attribuée à son offre est utilisée automatiquement pendant l'appel.
                    </p>
                </div>

                {/* Trame tabs */}
                <div className="flex items-center gap-2 overflow-x-auto pb-2" data-testid="trame-selector">
                    {trames.map((t) => {
                        const active = t.id === activeTrameId;
                        return (
                            <button
                                key={t.id}
                                onClick={() => setActiveTrameId(t.id)}
                                data-testid={`trame-tab-${t.id}`}
                                className={`group relative px-4 h-10 rounded-full text-xs font-bold border-2 transition whitespace-nowrap inline-flex items-center gap-2 ${
                                    active
                                        ? "bg-shariti-orange text-white border-shariti-orange shadow-btn"
                                        : "bg-white text-slate-600 border-shariti-gray hover:border-shariti-orange hover:text-shariti-orange"
                                }`}
                            >
                                {t.is_default && <Star className={`h-3 w-3 ${active ? "text-white" : "text-shariti-orange"}`} fill="currentColor" />}
                                {t.name}
                                <span className={`text-[9px] font-black uppercase tracking-widest ${active ? "text-white/80" : "text-slate-400"}`}>
                                    {t.items_count}
                                </span>
                            </button>
                        );
                    })}
                    <button
                        onClick={() => setTrameEditor({ mode: "create", name: "", description: "", copy_from_id: activeTrameId })}
                        data-testid="trame-create-btn"
                        className="inline-flex items-center gap-1.5 px-4 h-10 rounded-full text-xs font-bold border-2 border-dashed border-shariti-orange/40 text-shariti-orange hover:bg-shariti-orange-soft transition whitespace-nowrap"
                    >
                        <Plus className="h-3.5 w-3.5" />
                        Nouvelle trame
                    </button>
                </div>

                {/* Active trame summary + actions */}
                {activeTrame && (
                    <div className="card-soft p-5 flex flex-col md:flex-row md:items-center gap-4 justify-between">
                        <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                                <h2 className="text-lg font-extrabold text-shariti-black">{activeTrame.name}</h2>
                                {activeTrame.is_default && (
                                    <span className="text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full bg-shariti-orange-soft text-shariti-orange border border-shariti-orange/30">
                                        Trame par défaut
                                    </span>
                                )}
                                <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
                                    {items.length} questions
                                </span>
                            </div>
                            {activeTrame.description && (
                                <p className="mt-1 text-sm font-medium text-slate-500">{activeTrame.description}</p>
                            )}
                        </div>
                        <div className="flex items-center gap-2 flex-wrap">
                            <button
                                onClick={() => { setCreating(true); setEditing({ ...EMPTY_ITEM }); }}
                                data-testid="entretien-add-btn"
                                className="btn-shariti"
                            >
                                <Plus className="h-4 w-4" />
                                Ajouter une question
                            </button>
                            <button
                                onClick={() => setTrameEditor({ mode: "create", name: `Copie de ${activeTrame.name}`, description: "", copy_from_id: activeTrame.id })}
                                data-testid="trame-duplicate-btn"
                                className="inline-flex items-center gap-2 rounded-full bg-white border border-shariti-gray text-slate-600 hover:text-shariti-orange hover:border-shariti-orange px-4 h-10 font-bold text-xs transition"
                            >
                                <Copy className="h-4 w-4" />
                                Dupliquer
                            </button>
                            <button
                                onClick={() => setTrameEditor({ mode: "rename", name: activeTrame.name, description: activeTrame.description || "" })}
                                data-testid="trame-rename-btn"
                                className="inline-flex items-center gap-2 rounded-full bg-white border border-shariti-gray text-slate-600 hover:text-shariti-orange hover:border-shariti-orange px-4 h-10 font-bold text-xs transition"
                            >
                                <Pencil className="h-4 w-4" />
                                Renommer
                            </button>
                            <button
                                onClick={resetDefaults}
                                disabled={resetting}
                                data-testid="entretien-reset-btn"
                                className="inline-flex items-center gap-2 rounded-full bg-white border border-shariti-gray text-slate-600 hover:text-shariti-orange hover:border-shariti-orange px-4 h-10 font-bold text-xs transition"
                            >
                                {resetting ? <Loader2 className="h-4 w-4 animate-spin" /> : <RotateCcw className="h-4 w-4" />}
                                Restaurer
                            </button>
                            {!activeTrame.is_default && (
                                <button
                                    onClick={() => deleteTrame(activeTrame)}
                                    data-testid="trame-delete-btn"
                                    className="inline-flex items-center gap-2 rounded-full bg-white border border-red-200 text-shariti-red hover:bg-red-50 px-4 h-10 font-bold text-xs transition"
                                >
                                    <Trash2 className="h-4 w-4" />
                                    Supprimer
                                </button>
                            )}
                        </div>
                    </div>
                )}
            </header>

            {loadingItems ? (
                <div className="card-soft p-12 flex justify-center">
                    <Loader2 className="h-5 w-5 animate-spin text-shariti-orange" />
                </div>
            ) : (
                grouped.map(([phaseId, phaseItems]) => {
                    const phase = PHASE_BY_ID[phaseId] || { label: phaseId, color: "bg-slate-100 text-slate-600 border-slate-200" };
                    if (phaseItems.length === 0) return null;
                    return (
                        <section key={phaseId} className="card-soft p-5 md:p-6" data-testid={`admin-phase-${phaseId}`}>
                            <div className="flex items-center justify-between gap-3 mb-4">
                                <h2 className="text-sm md:text-base font-black uppercase tracking-widest text-shariti-black">
                                    {phase.label}
                                    <span className="ml-2 text-xs font-bold text-slate-400">({phaseItems.length})</span>
                                </h2>
                            </div>
                            <div className="space-y-2.5">
                                {phaseItems.map((item) => {
                                    const globalIdx = items.findIndex((i) => i.id === item.id);
                                    const kindLabel = KINDS.find((k) => k.id === item.kind)?.label || item.kind;
                                    const isDragging = draggedId === item.id;
                                    const isDropTarget = dropTargetId === item.id && draggedId && draggedId !== item.id;
                                    return (
                                        <motion.div
                                            key={item.id}
                                            layout
                                            initial={{ opacity: 0, y: 6 }}
                                            animate={{ opacity: isDragging ? 0.35 : 1, y: 0 }}
                                            draggable
                                            onDragStart={(e) => handleDragStart(e, item.id)}
                                            onDragOver={(e) => handleDragOver(e, item.id)}
                                            onDrop={(e) => handleDrop(e, item.id)}
                                            onDragEnd={handleDragEnd}
                                            className={`group flex items-start gap-3 rounded-2xl border p-4 bg-white transition select-none ${
                                                isDropTarget
                                                    ? "border-shariti-orange border-2 shadow-lg shadow-shariti-orange/10 ring-2 ring-shariti-orange/20"
                                                    : "border-shariti-gray hover:border-shariti-orange/40"
                                            } ${isDragging ? "cursor-grabbing" : "cursor-grab"}`}
                                            data-testid={`admin-item-${item.id}`}
                                        >
                                            <div
                                                className="flex flex-col items-center gap-0.5 pt-0.5"
                                                onMouseDown={(e) => e.stopPropagation()}
                                            >
                                                <button
                                                    onClick={() => move(item, "up")}
                                                    disabled={globalIdx === 0}
                                                    title="Monter"
                                                    className="h-6 w-6 inline-flex items-center justify-center rounded-md hover:bg-shariti-cream disabled:opacity-25"
                                                >
                                                    <ArrowUp className="h-3.5 w-3.5 text-slate-500" />
                                                </button>
                                                <div
                                                    title="Glisser pour réorganiser"
                                                    data-testid={`admin-item-${item.id}-drag-handle`}
                                                    className={`h-6 w-6 inline-flex items-center justify-center rounded-md hover:bg-shariti-orange-soft ${
                                                        isDragging ? "cursor-grabbing text-shariti-orange" : "cursor-grab text-slate-400 hover:text-shariti-orange"
                                                    }`}
                                                >
                                                    <GripVertical className="h-4 w-4" />
                                                </div>
                                                <button
                                                    onClick={() => move(item, "down")}
                                                    disabled={globalIdx === items.length - 1}
                                                    title="Descendre"
                                                    className="h-6 w-6 inline-flex items-center justify-center rounded-md hover:bg-shariti-cream disabled:opacity-25"
                                                >
                                                    <ArrowDown className="h-3.5 w-3.5 text-slate-500" />
                                                </button>
                                            </div>

                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-2 flex-wrap mb-1.5">
                                                    <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">
                                                        Q{globalIdx + 1}
                                                    </span>
                                                    <span className={`text-[9px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full border ${phase.color}`}>
                                                        {phase.label}
                                                    </span>
                                                    <span className="text-[9px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full bg-shariti-cream text-slate-500 border border-shariti-gray">
                                                        {kindLabel}
                                                    </span>
                                                    {(item.kind === "yesno_gating" || item.kind === "rating_gating") && (
                                                        <span className="text-[9px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200 inline-flex items-center gap-1">
                                                            <AlertOctagon className="h-2.5 w-2.5" /> Éliminatoire
                                                        </span>
                                                    )}
                                                    {item.criterion_ids?.length > 0 && (
                                                        <span className="text-[9px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full bg-shariti-orange-soft text-shariti-orange border border-shariti-orange/30 inline-flex items-center gap-1">
                                                            <Sparkles className="h-2.5 w-2.5" /> {item.criterion_ids.join(", ")}
                                                        </span>
                                                    )}
                                                    {item.with_comment && (
                                                        <span className="text-[9px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-200">
                                                            + commentaire
                                                        </span>
                                                    )}
                                                    {item.no_speak && (
                                                        <span className="text-[9px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full bg-slate-100 text-slate-500 border border-slate-200 italic">
                                                            non lu
                                                        </span>
                                                    )}
                                                </div>
                                                <div className="text-sm font-extrabold text-shariti-black leading-snug">
                                                    {item.q}
                                                </div>
                                                {item.hint && (
                                                    <p className="mt-1 text-[11px] font-medium text-slate-500">{item.hint}</p>
                                                )}
                                                {item.criterion_hint && (
                                                    <p className="mt-1 text-[11px] font-medium text-shariti-orange/80">{item.criterion_hint}</p>
                                                )}
                                            </div>

                                            <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition">
                                                <button
                                                    onClick={() => { setEditing({ ...item }); setCreating(false); }}
                                                    data-testid={`admin-item-${item.id}-edit`}
                                                    title="Modifier"
                                                    className="h-9 w-9 inline-flex items-center justify-center rounded-full bg-shariti-cream text-slate-500 hover:text-shariti-orange hover:bg-shariti-orange-soft transition"
                                                >
                                                    <Pencil className="h-4 w-4" />
                                                </button>
                                                <button
                                                    onClick={() => remove(item)}
                                                    data-testid={`admin-item-${item.id}-delete`}
                                                    title="Supprimer"
                                                    className="h-9 w-9 inline-flex items-center justify-center rounded-full bg-white text-shariti-red hover:bg-red-50 transition border border-red-100"
                                                >
                                                    <Trash2 className="h-4 w-4" />
                                                </button>
                                            </div>
                                        </motion.div>
                                    );
                                })}
                            </div>
                        </section>
                    );
                })
            )}

            {editing && (
                <ItemEditorModal
                    item={editing}
                    isCreating={creating}
                    saving={saving}
                    criteriaIds={criteriaIds}
                    onCancel={() => { setEditing(null); setCreating(false); }}
                    onSave={saveEdit}
                />
            )}

            {trameEditor && (
                <TrameEditorModal
                    state={trameEditor}
                    onCancel={() => setTrameEditor(null)}
                    onSave={(payload) => saveTrame(payload, trameEditor.mode)}
                />
            )}
        </div>
    );
}

function TrameEditorModal({ state, onCancel, onSave }) {
    const [name, setName] = useState(state.name || "");
    const [description, setDescription] = useState(state.description || "");

    function submit() {
        if (name.trim().length < 2) {
            toast.error("Nom trop court");
            return;
        }
        onSave({
            name: name.trim(),
            description: description.trim() || null,
            copy_from_id: state.copy_from_id || null,
        });
    }

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="fixed inset-0 z-50 bg-shariti-black/40 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={onCancel}
            data-testid="trame-editor-modal"
        >
            <motion.div
                initial={{ scale: 0.95, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                onClick={(e) => e.stopPropagation()}
                className="bg-white rounded-3xl shadow-2xl p-6 md:p-8 max-w-md w-full"
            >
                <div className="flex items-start justify-between gap-4 mb-5">
                    <div>
                        <div className="inline-flex items-center gap-2 rounded-full bg-shariti-orange-soft text-shariti-orange px-3 py-1 text-[10px] font-bold uppercase tracking-widest">
                            <ClipboardEdit className="h-3 w-3" />
                            {state.mode === "create" ? "Nouvelle trame" : "Renommer la trame"}
                        </div>
                        <h2 className="mt-3 text-xl font-black tracking-tight text-shariti-black">
                            {state.mode === "create" ? "Créer une trame" : "Éditer la trame"}
                        </h2>
                        {state.copy_from_id && state.mode === "create" && (
                            <p className="mt-1 text-[11px] font-semibold text-shariti-orange">
                                ✓ Les questions de la trame courante seront dupliquées dans la nouvelle.
                            </p>
                        )}
                    </div>
                    <button
                        onClick={onCancel}
                        className="h-9 w-9 inline-flex items-center justify-center rounded-full bg-shariti-cream text-slate-500 hover:text-shariti-red hover:bg-red-50 transition"
                    >
                        <X className="h-4 w-4" />
                    </button>
                </div>

                <div className="space-y-4">
                    <div>
                        <label className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1.5 block">
                            Nom de la trame
                        </label>
                        <input
                            type="text"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            data-testid="trame-editor-name"
                            placeholder="Ex : Trame missions itinérantes"
                            className="w-full bg-shariti-cream border border-shariti-gray rounded-xl px-3 py-2.5 text-sm font-medium text-shariti-black outline-none focus:border-shariti-orange focus:ring-2 focus:ring-shariti-orange/10"
                        />
                    </div>
                    <div>
                        <label className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1.5 block">
                            Description (optionnel)
                        </label>
                        <textarea
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            rows={3}
                            data-testid="trame-editor-description"
                            placeholder="Ex : Pour les missions où le candidat sillonne plusieurs villes en France. Logement et déplacement pris en charge."
                            className="w-full bg-shariti-cream border border-shariti-gray rounded-xl px-3 py-2.5 text-sm font-medium text-shariti-black outline-none focus:border-shariti-orange focus:ring-2 focus:ring-shariti-orange/10 resize-none"
                        />
                    </div>
                </div>

                <div className="mt-6 flex justify-end gap-2">
                    <button
                        onClick={onCancel}
                        className="inline-flex items-center gap-2 rounded-full bg-white border border-shariti-gray text-slate-600 hover:text-shariti-red hover:border-shariti-red px-5 h-10 font-bold text-xs transition"
                    >
                        Annuler
                    </button>
                    <button
                        onClick={submit}
                        data-testid="trame-editor-save"
                        className="btn-shariti"
                    >
                        <Save className="h-4 w-4" />
                        {state.mode === "create" ? "Créer la trame" : "Enregistrer"}
                    </button>
                </div>
            </motion.div>
        </motion.div>
    );
}

function ItemEditorModal({ item, isCreating, saving, criteriaIds, onCancel, onSave }) {
    const [form, setForm] = useState({
        phase: item.phase || "discovery",
        q: item.q || "",
        kind: item.kind || "rating",
        criterion_ids: item.criterion_ids || [],
        hint: item.hint || "",
        criterion_hint: item.criterion_hint || "",
        no_speak: !!item.no_speak,
        with_comment: !!item.with_comment,
    });
    const isRating = form.kind === "rating" || form.kind === "rating_gating";
    const supportsComment = form.kind === "yesno_gating";

    function update(partial) {
        setForm((f) => ({ ...f, ...partial }));
    }

    function toggleCriterion(cid) {
        setForm((f) => ({
            ...f,
            criterion_ids: f.criterion_ids.includes(cid)
                ? f.criterion_ids.filter((c) => c !== cid)
                : [...f.criterion_ids, cid],
        }));
    }

    function submit() {
        if (form.q.trim().length < 3) {
            toast.error("Question trop courte");
            return;
        }
        const payload = {
            phase: form.phase,
            q: form.q.trim(),
            kind: form.kind,
            criterion_ids: isRating ? form.criterion_ids : [],
            hint: form.hint || null,
            criterion_hint: isRating ? (form.criterion_hint || null) : null,
            no_speak: form.no_speak,
            with_comment: supportsComment ? form.with_comment : false,
        };
        onSave(payload);
    }

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="fixed inset-0 z-50 bg-shariti-black/40 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto"
            data-testid="admin-editor-modal"
            onClick={onCancel}
        >
            <motion.div
                initial={{ scale: 0.95, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                onClick={(e) => e.stopPropagation()}
                className="bg-white rounded-3xl shadow-2xl p-6 md:p-8 max-w-2xl w-full my-8"
            >
                <div className="flex items-start justify-between gap-4 mb-5">
                    <div>
                        <div className="inline-flex items-center gap-2 rounded-full bg-shariti-orange-soft text-shariti-orange px-3 py-1 text-[10px] font-bold uppercase tracking-widest">
                            {isCreating ? <Plus className="h-3 w-3" /> : <Pencil className="h-3 w-3" />}
                            {isCreating ? "Nouvelle question" : "Modifier la question"}
                        </div>
                        <h2 className="mt-3 text-xl font-black tracking-tight text-shariti-black">
                            Édition de la trame
                        </h2>
                    </div>
                    <button
                        onClick={onCancel}
                        className="h-9 w-9 inline-flex items-center justify-center rounded-full bg-shariti-cream text-slate-500 hover:text-shariti-red hover:bg-red-50 transition"
                    >
                        <X className="h-4 w-4" />
                    </button>
                </div>

                <div className="space-y-4">
                    <div>
                        <label className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1.5 block">
                            Question (à lire à voix haute)
                        </label>
                        <textarea
                            value={form.q}
                            onChange={(e) => update({ q: e.target.value })}
                            rows={3}
                            data-testid="editor-q"
                            placeholder="Ex : Comment réagissez-vous face à un refus dans la rue ?"
                            className="w-full bg-shariti-cream border border-shariti-gray rounded-xl px-3 py-2.5 text-sm font-medium text-shariti-black outline-none focus:border-shariti-orange focus:ring-2 focus:ring-shariti-orange/10 resize-none"
                        />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div>
                            <label className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1.5 block">
                                Phase
                            </label>
                            <select
                                value={form.phase}
                                onChange={(e) => update({ phase: e.target.value })}
                                data-testid="editor-phase"
                                className="w-full bg-shariti-cream border border-shariti-gray rounded-xl px-3 py-2.5 text-sm font-medium text-shariti-black outline-none focus:border-shariti-orange focus:ring-2 focus:ring-shariti-orange/10"
                            >
                                {PHASES.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
                            </select>
                        </div>
                        <div>
                            <label className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1.5 block">
                                Type de réponse
                            </label>
                            <select
                                value={form.kind}
                                onChange={(e) => update({ kind: e.target.value })}
                                data-testid="editor-kind"
                                className="w-full bg-shariti-cream border border-shariti-gray rounded-xl px-3 py-2.5 text-sm font-medium text-shariti-black outline-none focus:border-shariti-orange focus:ring-2 focus:ring-shariti-orange/10"
                            >
                                {KINDS.map((k) => <option key={k.id} value={k.id}>{k.label}</option>)}
                            </select>
                        </div>
                    </div>

                    {isRating && (
                        <div>
                            <label className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1.5 block">
                                Critères Shariti V2 couverts par la note
                            </label>
                            <div className="flex flex-wrap gap-1.5">
                                {criteriaIds.map((cid) => {
                                    const active = form.criterion_ids.includes(cid);
                                    return (
                                        <button
                                            key={cid}
                                            type="button"
                                            onClick={() => toggleCriterion(cid)}
                                            data-testid={`editor-crit-${cid}`}
                                            className={`text-[10px] font-bold uppercase tracking-widest px-2.5 py-1 rounded-full border-2 transition ${
                                                active
                                                    ? "bg-shariti-orange text-white border-shariti-orange"
                                                    : "bg-white text-slate-500 border-shariti-gray hover:border-shariti-orange hover:text-shariti-orange"
                                            }`}
                                        >
                                            {cid}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    <div>
                        <label className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1.5 block">
                            Indice de cadrage (optionnel)
                        </label>
                        <input
                            type="text"
                            value={form.hint}
                            onChange={(e) => update({ hint: e.target.value })}
                            data-testid="editor-hint"
                            placeholder="Ex : Adapter au contexte de l'offre."
                            className="w-full bg-shariti-cream border border-shariti-gray rounded-xl px-3 py-2.5 text-sm font-medium text-shariti-black outline-none focus:border-shariti-orange focus:ring-2 focus:ring-shariti-orange/10"
                        />
                    </div>

                    {isRating && (
                        <div>
                            <label className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1.5 block">
                                Grille de notation (optionnel)
                            </label>
                            <input
                                type="text"
                                value={form.criterion_hint}
                                onChange={(e) => update({ criterion_hint: e.target.value })}
                                data-testid="editor-criterion-hint"
                                placeholder="Ex : 1 = pas du tout / 5 = très à l'aise."
                                className="w-full bg-shariti-cream border border-shariti-gray rounded-xl px-3 py-2.5 text-sm font-medium text-shariti-black outline-none focus:border-shariti-orange focus:ring-2 focus:ring-shariti-orange/10"
                            />
                        </div>
                    )}

                    <div className="flex flex-wrap items-center gap-3">
                        {supportsComment && (
                            <label className="inline-flex items-center gap-2 text-xs font-semibold text-slate-600 cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={form.with_comment}
                                    onChange={(e) => update({ with_comment: e.target.checked })}
                                    data-testid="editor-with-comment"
                                    className="h-4 w-4 accent-shariti-orange"
                                />
                                Activer un champ commentaire libre sous le Oui/Non
                            </label>
                        )}
                        <label className="inline-flex items-center gap-2 text-xs font-semibold text-slate-600 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={form.no_speak}
                                onChange={(e) => update({ no_speak: e.target.checked })}
                                data-testid="editor-no-speak"
                                className="h-4 w-4 accent-shariti-orange"
                            />
                            Ne pas lire à voix haute (observation interne)
                        </label>
                    </div>
                </div>

                <div className="mt-6 flex justify-end gap-2">
                    <button
                        onClick={onCancel}
                        className="inline-flex items-center gap-2 rounded-full bg-white border border-shariti-gray text-slate-600 hover:text-shariti-red hover:border-shariti-red px-5 h-10 font-bold text-xs transition"
                    >
                        Annuler
                    </button>
                    <button
                        onClick={submit}
                        disabled={saving}
                        data-testid="editor-save"
                        className="btn-shariti"
                    >
                        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                        {isCreating ? "Créer la question" : "Enregistrer"}
                    </button>
                </div>
            </motion.div>
        </motion.div>
    );
}
